import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, DOCUMENT, effect, inject, Injector, OnDestroy } from '@angular/core';
import { filter, map, of, take, timeout } from 'rxjs';
import { AlertComponent } from 'ngx-bootstrap/alert';
import { AppSyncService } from '@app/common/services/app-sync.service';
import {
    NavigationCancel,
    NavigationCancellationCode,
    NavigationEnd,
    NavigationError,
    NavigationSkipped,
    Router,
    Event as RouterEvent,
    RouterOutlet,
} from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ToastComponent } from '@app/shared/layout/toast/toast.component';
import { createScrollActivity, listenToScroll } from '@app/shared/directives/ic-scrollbar.directive';

/** The longest the boot loader waits for the first navigation to settle before it goes regardless. */
export const BOOT_LOADER_MAX_WAIT_MS = 15_000;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        AlertComponent,
        RouterOutlet,
        ToastComponent
    ]
})
export class AppComponent implements OnDestroy {
    private appSyncService = inject(AppSyncService);
    private readonly document = inject(DOCUMENT);
    private readonly injector = inject(Injector);
    private readonly router = inject(Router);

    title = 'sa';

    public readonly isIE: boolean;

    /** Window scroll activity; while it is set, <html> carries is-scrolling and its scrollbar thumb is painted. */
    protected readonly viewportScroll = createScrollActivity();

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor() {
        this.isIE = /msie\s|trident\//i.test(window.navigator.userAgent);

        // The document viewport is the scroller on every page that is not a split page, under every layout. The root
        // component lives as long as the app, so it feeds the window's scroll events to the app scrollbar's
        // show-on-scroll timer (ER-1539). defaultView is non-null: the app only runs in a browser window.
        listenToScroll(this.document.defaultView!, this.viewportScroll);
        effect(() => this.setViewportScrolling(this.viewportScroll.isScrolling()));
        inject(DestroyRef).onDestroy(() => this.setViewportScrolling(false));

        this.removeBootLoaderAfterFirstNavigation();
    }

    ngOnDestroy() {
        this.appSyncService.stopConnection();
    }

    // =========================================================================================================================================================
    // Helper Methods
    // =========================================================================================================================================================

    /**
     * Takes away the boot loader src/index.html paints beside app-root once the first navigation has settled and the
     * page it landed on has rendered, so the loader hands straight over to the page with no blank frame between them
     * (ER-1572). A redirect or a superseding navigation has another navigation behind it, so the wait carries on to
     * that one; any other ending removes the loader, an error included, so it never covers the app for good.
     */
    private removeBootLoaderAfterFirstNavigation(): void {
        const removeNow = (): void => this.document.querySelector('.app-loader')?.remove();
        const removeAfterRender = (): void => {
            afterNextRender(removeNow, { injector: this.injector });
        };

        // A blocking initial navigation would have ended before this component existed, with no event left to wait for.
        if (this.router.navigated) {
            removeAfterRender();
            return;
        }

        // A guard or lazy chunk that never answers leaves the first navigation open for good. The loader then goes after
        // BOOT_LOADER_MAX_WAIT_MS, at once rather than after a render, since nothing may render: it never covers the app
        // for longer than a blank page would have.
        this.router.events
            .pipe(
                filter((event: RouterEvent) => isSettledNavigation(event)),
                take(1),
                map(() => true),
                timeout({ first: BOOT_LOADER_MAX_WAIT_MS, with: () => of(false) }),
                takeUntilDestroyed(),
            )
            .subscribe((settled: boolean) => settled ? removeAfterRender() : removeNow());
    }

    /**
     * Sets is-scrolling on <html> and makes Chromium repaint the root scrollbar, which it does not do when only a
     * class on <html> changes: the thumb would never show, or stay painted after it should hide. A synchronous
     * overflow round trip with a forced layout in between rebuilds the root scrollbar with the new styles before
     * the next paint. The scroll position is put back only if the round trip moved it, so an in-flight smooth
     * scroll is not cut short. Nothing happens when the class already has the requested state.
     */
    private setViewportScrolling(isScrolling: boolean): void {
        const root = this.document.documentElement;
        if (root.classList.contains('is-scrolling') === isScrolling) {
            return;
        }

        root.classList.toggle('is-scrolling', isScrolling);

        const view = this.document.defaultView!;
        const { scrollX, scrollY } = view;
        const overflow = root.style.overflow;
        root.style.overflow = 'hidden';
        void root.offsetHeight;
        root.style.overflow = overflow;

        if (view.scrollX !== scrollX || view.scrollY !== scrollY) {
            view.scrollTo(scrollX, scrollY);
        }
    }

}

/** True for the event that ends a navigation with no other navigation queued behind it. */
function isSettledNavigation(event: RouterEvent): boolean {
    if (event instanceof NavigationCancel) {
        return event.code !== NavigationCancellationCode.Redirect && event.code !== NavigationCancellationCode.SupersededByNewNavigation;
    }

    return event instanceof NavigationEnd || event instanceof NavigationError || event instanceof NavigationSkipped;
}
